/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectTerminalDetails } from '../../../../store';
import { setSelectedTerminalTabIndex } from '../../../../store/ui/actions';
import { getHaloNetSpaceNetworks, getOtherTerminalOnAircraft } from '../../../../utils/TerminalUtilities';
import TerminalIconMarker from '../../../fleet/TerminalIconMarker';
import OrbitPathsContainer from '../OrbitPathsContainer';
import { isNil } from 'lodash';

export const FULL_SPACE_VEHICLE_VIEW_MAP_HEIGHT = '481px';

interface FullSpaceVehicleViewMapContainerProps {
  map: google.maps.Map;
}

const FullSpaceVehicleViewMapContainer = ({ map }: FullSpaceVehicleViewMapContainerProps) => {
  // 3rd Party Hooks
  const dispatch = useDispatch();

  //selectors
  const vehicle = useSelector(selectTerminalDetails, shallowEqual);

  // A new array on each render would make TerminalIconMarker redraw its tail tag every time
  const spaceNetworks = useMemo(() => getHaloNetSpaceNetworks(vehicle, getOtherTerminalOnAircraft(vehicle)), [vehicle]);

  const selectVehicle = (datapathUuid: string, terminalTabIndex: number, isDoubleClick: boolean = false) => {
    if (isDoubleClick) {
      dispatch(setSelectedTerminalTabIndex(terminalTabIndex));
    }
  };

  return (
    <>
      {vehicle && !isNil(vehicle.latitudeDeg) && !isNil(vehicle.longitudeDeg) && (
        <TerminalIconMarker
          key={vehicle.datapathUuid}
          datapathUuid={vehicle.datapathUuid}
          map={map as unknown as google.maps.Map}
          lat={vehicle.latitudeDeg}
          lng={vehicle.longitudeDeg}
          connectivityStatus={vehicle.connectivityStatus}
          headingDeg={vehicle.headingDeg}
          aircraftTailNumber={vehicle.aircraftTailNumber}
          networkOrigin={vehicle.networkOrigin}
          beamDisplayName={vehicle.beamDisplayName}
          flDataRateBps={vehicle.flDataRateBps}
          rlDataRateBps={vehicle.rlDataRateBps}
          flightPhase={vehicle.flightPhase}
          flSignalQuality={vehicle.flSignalQuality}
          altitudeFeet={vehicle.altitudeFeet}
          altitudeUnit={'ft'}
          platformType={vehicle.platformType}
          terminalTypeLabel={vehicle.terminalTypeLabel}
          networkGroup={vehicle.networkGroup}
          installationType={vehicle.installationType}
          selected={false}
          selectTerminal={selectVehicle}
          selectedTerminalTabIndex={0}
          otherTerminalsOnAircraft={null}
          otherTerminalOnAircraftSelectedId={null}
          isTailTagTransparent={false}
          sourceTime={vehicle.sourceTime}
          isTailTagShown={true}
          isFullTailViewEnabled={false}
          isPrimaryTerminal={false}
          isSecondaryTerminal={false}
          useGreatestSteerableBeamPriorityIconOfAllTerminalsOnAircraft
          hcxShortestDistanceToBeamEdge={0}
          areAnyTerminalsOnAircraftUsingSteerableBeam={false}
          disableAntennaTags={false}
          lwSiteId={vehicle.lwSiteId}
          lwTxPowerDb={vehicle.lwTxPowerDb}
          spaceNetworks={spaceNetworks}
        />
      )}
      <OrbitPathsContainer map={map} isOnFullVehicleView />
    </>
  );
};

export default FullSpaceVehicleViewMapContainer;
